import React, { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { engagementModels } from "../data/siteContent";
import "../styles/domain-pages.css";
import "../styles/services.css";

const servicePillars = [
  {
    id: "advisory",
    navLabel: "Advisory & Roadmap",
    number: "01",
    title: "Data Strategy & Platform Review",
    audience: "For CFOs, CIOs and Operations Directors who need clarity across systems, data flows and operational bottlenecks before spending capital.",
    badges: ["Microsoft Fabric", "Azure Architecture", "Data Governance Frameworks"],
    capabilities: [
      "Current-State System Review: map operational systems, reporting processes, data sources, manual workarounds and integration risks.",
      "Fabric & Azure Readiness Assessments: evaluate data maturity, governance structures and system pipelines to map a pragmatic migration roadmap.",
      "Architecture Advisory: define data governance, security controls, data contracts, ownership paths and SLA planning.",
    ],
    whenHelpful: [
      "Reports, extracts or operational handoffs are inconsistent across teams.",
      "You are considering Fabric, Azure, Snowflake or warehouse investment and need a practical roadmap.",
      "Leadership needs a second opinion before committing implementation budget.",
    ],
    deliverables: [
      "Current-state system and data flow map",
      "Reporting, reconciliation and governance risk register",
      "Target architecture and prioritised delivery roadmap",
    ],
    outputs: [
      "Architecture diagram",
      "Integration backlog",
      "Implementation phasing",
      "Decision pack",
    ],
  },
  {
    id: "data-engineering",
    navLabel: "Data Platforms",
    number: "02",
    title: "Data Platform & Pipeline Delivery",
    audience: "For IT Managers, operations leads and business intelligence teams that need robust backend pipelines and connected data foundations.",
    badges: [
      "Microsoft Fabric",
      "Azure Synapse",
      "Medallion Architecture",
      "Batch & Real-Time Ingestion",
      "Databricks",
    ],
    capabilities: [
      "Data Foundation Design: engineer structured, scalable data lakehouse, warehouse and semantic layers for operational and reporting use cases.",
      "Multi-Speed Data Ingestion: design robust data movement across APIs, files, databases, real-time streams and high-scale batch ingestion.",
      "Modern Warehouse & Lakehouse Platforms: implement, model and tune Azure Synapse Analytics, Snowflake, Microsoft Fabric and related data platforms.",
    ],
    whenHelpful: [
      "Critical business data is split across CRMs, finance systems, files, databases and SaaS tools.",
      "Manual extracts or scripts are carrying operational, finance or reporting processes.",
      "You need production-grade pipelines with validation, ownership and monitoring.",
    ],
    deliverables: [
      "Source-to-target pipeline design and implementation",
      "Data quality checks, reconciliation tables and exception logs",
      "Curated warehouse, lakehouse and semantic-ready models",
    ],
    outputs: [
      "Ingestion framework",
      "Validated datasets",
      "Monitoring design",
      "Handover notes",
    ],
  },
  {
    id: "automation-ai",
    navLabel: "Operational Automation",
    number: "03",
    title: "Operational Data Integration & Automation",
    audience: "For COOs and business unit leaders looking to reduce manual labour and connect recurring workflows across systems.",
    badges: ["Power Apps", "Power Platform", "Power Automate", "Azure AI Foundry", "Copilot AI Agents"],
    capabilities: [
      "Operational Workflow Automation: replace manual extracts, approvals, evidence capture and spreadsheet handoffs with controlled workflows.",
      "Custom Power Apps Development: build secure internal low-code mobile and web applications to replace manual data entry and legacy forms.",
      "End-to-End Data Movement: use Power Automate, Azure Data Factory, APIs and write-back workflows to move data between analytics and operational apps.",
    ],
    whenHelpful: [
      "Teams are manually copying data between spreadsheets, emails and business applications.",
      "Approval, exception handling or evidence capture depends on people remembering each step.",
      "Operational systems need write-back, notifications or controlled workflow automation.",
    ],
    deliverables: [
      "Workflow and exception process redesign",
      "Power Apps, Power Automate or API-based automation build",
      "Write-back logic, approval states and operational audit trail",
    ],
    outputs: [
      "Workflow map",
      "Automation backlog",
      "App prototype",
      "Control log",
    ],
  },
  {
    id: "cloud-integration",
    navLabel: "Cloud Integrations",
    number: "04",
    title: "Cloud Integration & Pipeline Reliability",
    audience: "For CTOs, DevOps leads and operational teams that need dependable system connections and observable data movement.",
    badges: ["Azure Cloud", "REST & GraphQL APIs", "Azure Data Factory", "dbt", "Airbyte", "Fivetran"],
    capabilities: [
      "Secure Cloud Foundations: set up governed, scalable and cost-optimised environments across Azure, AWS and hybrid ecosystems.",
      "Broad Integration Coverage: build resilient REST, GraphQL and SFTP integrations connecting Xero, HubSpot, Salesforce, ERPs, field systems and other business apps into the data core.",
      "Pipeline Reliability & Observability: add monitoring, alerting dashboards and auto-retry logic to reduce pipeline breakages and operational noise.",
    ],
    whenHelpful: [
      "System integrations fail silently or depend on manual file checks.",
      "APIs, SFTP feeds or third-party data sources need secure production handling.",
      "Platform owners need better observability, retry logic and incident visibility.",
    ],
    deliverables: [
      "Secure API, SFTP and cloud integration patterns",
      "Alerting, retry and exception handling design",
      "Operational ownership model for data movement and support",
    ],
    outputs: [
      "Integration specification",
      "Runbook",
      "Alerting rules",
      "Support model",
    ],
  },
];

const deliveryStandards = [
  "Source-to-target lineage",
  "Data quality checks",
  "Reconciliation controls",
  "Monitoring and alerting",
  "Security and access patterns",
  "Handover documentation",
];

export default function DataEngineeringServices() {
  const [openPillar, setOpenPillar] = useState(servicePillars[0].id);

  return (
    <div className="site-page services-page">
      <Seo
        title="Data Engineering, Operational Integration & Automation Services | Data Apparatus"
        description="Data engineering, operational integration and automation services across advisory, Azure, Fabric, data platforms, APIs, workflow automation, semantic BI and observability."
      />

      <section className="page-hero services-hero">
        <div className="page-hero-copy">
          <span className="eyebrow">Data engineering, integration and automation services</span>
          <h1>Connect operational systems into trusted data foundations.</h1>
          <h2>
            We engineer robust cloud data platforms, automated business workflows and
            decision-ready foundations for growing Australian enterprises.
          </h2>
          <div className="hero-actions">
            <Link to="/contact" className="primary-btn">
              Book a platform review
            </Link>
            <a href="#ways-to-work" className="secondary-btn">
              Explore services
            </a>
          </div>
        </div>
      </section>

      <nav className="services-jump-nav" aria-label="Services sections">
        <a href="#ways-to-work">Ways to work</a>
        {servicePillars.map((pillar) => (
          <a href={`#${pillar.id}`} key={pillar.id}>
            {pillar.navLabel}
          </a>
        ))}
      </nav>

      <section className="work-models-section" id="ways-to-work">
        <div className="work-models-inner">
          <div className="work-models-copy">
            <span className="eyebrow">Ways to work with us</span>
            <h2>Choose the delivery model that matches your system and workflow maturity.</h2>
            <p>
              Some teams need a sharp review before they invest. Others need delivery
              ownership across systems, pipelines or workflows. Data Apparatus can support
              each stage without forcing a heavy engagement model too early.
            </p>
          </div>

          <div className="work-models-grid">
            {engagementModels.map((model) => (
              <article
                className={`work-model-card ${model.featured ? "featured" : ""}`}
                key={model.title}
              >
                <span className="work-model-number">{model.number}</span>
                <h3>{model.title}</h3>
                <p>{model.summary}</p>
                <ul>
                  {model.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <strong>{model.fit}</strong>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="services-accordion-section">
        <div className="section-heading">
          <span className="eyebrow">Service pillars</span>
          <h2>Choose the capability area that matches your current system, workflow or data problem.</h2>
        </div>

        <div className="services-accordion">
          {servicePillars.map((pillar) => {
            const isOpen = openPillar === pillar.id;
            return (
              <article
                className={`service-pillar ${isOpen ? "open" : ""}`}
                id={pillar.id}
                key={pillar.id}
              >
                <button
                  className="service-pillar-toggle"
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpenPillar(isOpen ? "" : pillar.id)}
                >
                  <span className="pillar-title">
                    <em>{pillar.number}</em>
                    {pillar.title}
                  </span>
                  <span className="pillar-badges">
                    {pillar.badges.slice(0, 4).map((badge) => (
                      <i key={badge}>{badge}</i>
                    ))}
                  </span>
                  <span className="pillar-action">
                    {isOpen ? "Hide details" : "View details"}
                    <b aria-hidden="true">{isOpen ? "-" : "+"}</b>
                  </span>
                </button>

                {isOpen && (
                  <div className="service-pillar-body">
                    <div className="pillar-editorial">
                      <p className="pillar-audience">{pillar.audience}</p>
                      <div className="pillar-editorial-section">
                        <div>
                          <h4>What we deliver</h4>
                          <ul>
                            {pillar.deliverables.map((deliverable) => (
                              <li key={deliverable}>{deliverable}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="pillar-editorial-section">
                        <div>
                          <h4>When this helps</h4>
                          <ul>
                            {pillar.whenHelpful.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <aside className="pillar-aside">
                      <div className="pillar-capability-block">
                        <h4>Core capability</h4>
                        <ul>
                          {pillar.capabilities.map((capability) => (
                            <li key={capability}>{capability}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="pillar-output-group">
                        <h4>Typical outputs</h4>
                        <div className="pillar-output-strip" aria-label="Typical engagement outputs">
                          {pillar.outputs.map((output) => (
                            <span key={output}>{output}</span>
                          ))}
                        </div>
                      </div>
                      <div className="pillar-all-badges">
                        {pillar.badges.map((badge) => (
                          <span key={badge}>{badge}</span>
                        ))}
                      </div>
                    </aside>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="delivery-standards-section">
        <div className="section-heading">
          <span className="eyebrow">Delivery standards</span>
          <h2>Every build is designed to be operated, audited and improved.</h2>
          <p>
            The exact tools can change. The delivery discipline stays consistent across
            cloud platforms, operational systems, automation workflows and reporting layers.
          </p>
        </div>
        <div className="delivery-standards-grid">
          {deliveryStandards.map((standard) => (
            <div key={standard}>{standard}</div>
          ))}
        </div>
      </section>

      <section className="cta-band">
        <h2>Ready to modernise your data platform, integrations or reporting foundation?</h2>
        <p>
          Let&apos;s map your systems, identify data movement risks, and define the right
          delivery path across cloud platforms, operational systems, automation workflows
          and reporting layers.
        </p>
        <Link to="/contact" className="primary-btn">
          Book a data platform review
        </Link>
      </section>

      <Footer />
    </div>
  );
}
